import React, { useEffect, useState } from 'react';
import { Select } from 'antd';
import './index.scss';
import {
    ALL_SUBCATEGORIES,
    ALL_BEHAVIORS,
    getCategory,
    getSubCategory,
    getBehavior,
    categoryOptions,
    subCategoryOptions,
    behaviorOptions,
    resolveScopeLevel,
    HEADER_TEXT,
} from './constants';
import SectionHeader from '../../../SectionHeader';
import AsyncSection from '../../../../../../../../Components/AsyncSection';
import SVGIcons from '../../../../../../../../Components/SVGIcons';
import { EmptyState } from '../../../../../../../../Packages/EmptyState';
import { buildBenchmarkFiltersPayload } from '../../../../utils/buildBenchmarkFiltersPayload';
import { toast } from '../../../../../../../../utils/messageUtils';


const ShieldIcon = () => (
    <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
        <path d='M12 3l7 2.6v5.2c0 4.3-2.9 7.4-7 8.9-4.1-1.5-7-4.6-7-8.9V5.6z' />
        <path d='M9 12l2 2 4-4' />
    </svg>
);

// Mirrors the real gist card's and comment cards' frames so the skeleton
// occupies the same footprint as the content it's standing in for.
const SampleCommentsScopeSkeleton = () => (
    <>
        <div className='samc__gist samc__gist--skeleton'>
            <div className='samc__gist-head'>
                <span className='skeleton-block skeleton-block--badge' />
                <span className='skeleton-block skeleton-block--tag' />
            </div>
            <span className='skeleton-block skeleton-block--line' />
            <span className='skeleton-block skeleton-block--line skeleton-block--line-short' />
        </div>
        <div className='samc__comments samc__comments--skeleton'>
            {[0, 1, 2].map((i) => (
                <div className='samc__comment' key={i}>
                    <span className='skeleton-block skeleton-block--icon' />
                    <span className='skeleton-block skeleton-block--line' />
                </div>
            ))}
        </div>
    </>
);

const SampleComments = (props) => {
    const { categoryKey, subKey, behaviorKey } = props.sampleCommentsFilters;
    const [scopeLoading, setScopeLoading] = useState(false);
    const taxonomy = props.fetchBenchmarkTaxonomyApi.data && props.fetchBenchmarkTaxonomyApi.data.data || [];
    const category = getCategory(taxonomy, categoryKey);
    const subCategory = getSubCategory(category, subKey);

    // The taxonomy is org-specific (fetched separately), so it can resolve
    // after this mounts — once it lands, default to its first category/
    // sub-category if nothing's selected yet.
    useEffect(() => {
        if (!categoryKey && taxonomy.length > 0) {
            const firstCategory = taxonomy[0];
            const firstSub = firstCategory.children && firstCategory.children[0];
            props.handleChangeSampleCommentsFilter({
                categoryKey: firstCategory.value,
                subKey: ALL_SUBCATEGORIES,
                behaviorKey: ALL_BEHAVIORS,
            });
            fetchGistForScope(firstCategory.value, ALL_SUBCATEGORIES, ALL_BEHAVIORS);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taxonomy.length, categoryKey]);

    // Re-fetches the gist/comments for a drill-down selection. categoryName/
    // subCategoryName/behaviorText come straight off the taxonomy node — the
    // same fields the backend expects in getCommentsAndGist's scope payload.
    const fetchGistForScope = (nextCategoryKey, nextSubKey, nextBehaviorKey) => {
        const nextCategory = getCategory(taxonomy, nextCategoryKey);
        const nextSubCategory = getSubCategory(nextCategory, nextSubKey);
        const nextBehavior = getBehavior(nextSubCategory, nextBehaviorKey);
        if (!nextCategory) return;

        setScopeLoading(true);
        props.getCommentsAndGist(
            buildBenchmarkFiltersPayload(props.benchmarkFiltersAnalysis),
            (isSuccess) => {
                setScopeLoading(false);
                if (!isSuccess) {
                    toast.error('Could not refresh the gist for this selection.');
                }
            },
            {
                categoryName: nextCategory.categoryName,
                ...(nextSubCategory ? { subCategoryName: nextSubCategory.subCategoryName } : {}),
                ...(nextBehavior ? { behaviorText: nextBehavior.behaviorText } : {}),
            }
        );
    };

    // Changing a higher level resets the levels below it.
    const handleCategoryChange = (value) => {
        const next = getCategory(taxonomy, value);
        const nextSubKey = next.children && next.children.length ? next.children[0].value : ALL_SUBCATEGORIES;
        props.handleChangeSampleCommentsFilter({
            categoryKey: value,
            subKey: ALL_SUBCATEGORIES,
            behaviorKey: ALL_BEHAVIORS,
        });
        fetchGistForScope(value, ALL_SUBCATEGORIES, ALL_BEHAVIORS);
    };

    const handleSubChange = (value) => {
        props.handleChangeSampleCommentsFilter({ subKey: value, behaviorKey: ALL_BEHAVIORS });
        fetchGistForScope(categoryKey, value, ALL_BEHAVIORS);
    };

    const handleBehaviorChange = (value) => {
        props.handleChangeSampleCommentsFilter({ behaviorKey: value });
        fetchGistForScope(categoryKey, subKey, value);
    };

    const gistData = props.fetchCommentsAndGistApi.data || {};
    const scopeLevel = resolveScopeLevel(subCategory, behaviorKey);
    const hasGist = !!(gistData.gist && gistData.gist.available);
    const hasComments = !!(gistData.comments && gistData.comments.length);

    return (
        <AsyncSection api={props.fetchBenchmarkTaxonomyApi}>
            <SectionHeader
                title={HEADER_TEXT.title}
                description={HEADER_TEXT.description}
            />
        <div className='cardwrapper'>
            <div className='samc'>
                <div className='samc__header'>
                    <div className='samc__title'>Sample Comments</div>
                    <span className='samc__anon'>
                        <ShieldIcon />
                        Anonymized — no names
                    </span>
                </div>
                <div className='samc__subtitle'>
                    Drill down from category to sub-category to behavior to see a summary gist and
                    representative comments.
                </div>

                    {!category ? (
                        <SampleCommentsScopeSkeleton />
                    ) : (
                        <>
                                {/* Cascading filters */}
                                <div className='samc__filters'>
                                    <div className='samc__filter'>
                                        <span className='samc__filter-label'>Category</span>
                                        <Select
                                            size='large'
                                            value={categoryKey}
                                            onChange={handleCategoryChange}
                                            options={categoryOptions(taxonomy)}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div className='samc__filter'>
                                        <span className='samc__filter-label'>Sub-Category</span>
                                        <Select
                                            size='large'
                                            value={subKey}
                                            onChange={handleSubChange}
                                            options={subCategoryOptions(category)}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div className='samc__filter'>
                                        <span className='samc__filter-label'>Behavior</span>
                                        <Select
                                            size='large'
                                            value={behaviorKey}
                                            onChange={handleBehaviorChange}
                                            options={behaviorOptions(subCategory)}
                                            disabled={subKey === ALL_SUBCATEGORIES}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                </div>

                                {scopeLoading ? (
                                    <SampleCommentsScopeSkeleton />
                                ) : !hasGist && !hasComments ? (
                                    <EmptyState component='filter' emptyStateMessage='No comments available for this selection.' />
                                ) : (
                                    <>
                                                {/* AI gist for the current scope */}
                                                {hasGist && <div className='samc__gist'>
                                                    <div className='samc__gist-head'>
                                                        <span className='samc__gist-badge'>
                                                            <SVGIcons type={"SVG-giststar"} />
                                                            Gist
                                                        </span>
                                                        <span className='samc__gist-tag'>
                                                            {scopeLevel} · {category.categoryName.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div className='samc__gist-text'>{gistData.gist.text}</div>
                                                </div>}

                                                {/* Representative comments */}
                                                {hasComments && <div className='samc__comments'>
                                                    {gistData.comments.map((comment) => (
                                                        <div className='samc__comment' key={comment.commentId}>
                                                            <span className='samc__comment-icon'>
                                                                <SVGIcons type={"SVG-Comments-content"} />
                                                            </span>
                                                            <span className='samc__comment-text'>"{comment.text}"</span>
                                                        </div>
                                                    ))}
                                    </div>}
                                </>
                            )}
                        </>
                    )}

                <div className='samc__note'>
                    Comments have been reviewed to remove names of individuals, teams, clients, and
                    other proper nouns to protect confidentiality.
                </div>
            </div>
        </div>
        </AsyncSection>
    );
};

export default SampleComments;
